import React, { useCallback, useEffect, useMemo, useReducer } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Grid, GridColumn as Column, GridToolbar } from '@progress/kendo-react-grid';
import FilterPanel from '@components/FilterPanel';
import SearchPanel from '@components/SearchPanel';
import I18n from '@utils/i18n';
import { KendoLocalizationProvider } from '@contexts/kendo-localization-context';
import api from '@utils/axios';
import useDebounce from '@hooks/useDebounce';
import { datatableWorkOrdersPath } from 'routes';
import { saveCacheToLocalStorage } from '@helpers/localStorage';
import kendoDatatableParams from '@helpers/kendoDatatableParams';
import EnterKeyPager from '@components/Kendo/EnterKeyPager';
import EditVisit from '@features/WorkOrders/VisitsSection/EditVisitModalTrigger';
import ColumnMenu from './nested/ColumnMenu';
import ColumnMenuContext from './nested/ColumnMenuContext';
import { getInitialData, getInitialFilters } from './helpers/initialData';
import './index.css';
import { usePublishListContext } from '@features/AIChatWidget/hooks/usePublishListContext';
import { actionTypes, reducer } from './reducer';
import { ALL_COLUMNS, ALL_COLUMNS_DATA, CACHE_KEY as DEFAULT_CACHE_KEY } from './constants';
import CustomScrollbar from '../../../components/CustomScrollbar/CustomScrollbar';
import MassActions from './nested/MassActions';
import useFilters from './hooks/useFilters';

function Index({
  workOrderTypesForSelect,
  workOrderStatusesForSelect,
  subcontractorsForSelect,
  visitStatusesForSelect,
  hasShowAllServiceReportsInCustomerPortal,
  buildingId,
  inspectionId,
  accountId,
  subcontractorsEnabled,
  cacheKey
}) {
  const initialData = useMemo(
    () => getInitialData({ cacheKey, buildingId, accountId, inspectionId }),
    [cacheKey, buildingId, accountId, inspectionId]
  );
  const [state, dispatch] = useReducer(reducer, {
    data: [],
    totalCount: 0,
    ...initialData
  });

  const { page, sort, filters, columns, data, totalCount, loading, selectAll, search } = state;

  // Publish live grid state to the AI chat widget (page context overlay).
  usePublishListContext({ page, sort, filters, totalCount, data, search });

  const debouncedFilters = useDebounce(filters);

  const filtersList = useFilters({
    filters,
    dispatch,
    workOrderTypesForSelect,
    workOrderStatusesForSelect,
    subcontractorsForSelect,
    visitStatusesForSelect,
    buildingId,
    inspectionId,
    accountId,
    subcontractorsEnabled
  });

  useEffect(() => {
    saveCacheToLocalStorage(cacheKey, { page, sort, filters, columns });
  }, [page, sort, filters, columns, cacheKey]);

  const onSearchChange = useCallback((value) => {
    dispatch({ type: actionTypes.SEARCH_CHANGED, search: value });
  }, []);

  useEffect(() => {
    const handleVisitUpdate = () => {
      dispatch({ type: actionTypes.DATA_CHANGED });
    };

    window.addEventListener('visitUpdated', handleVisitUpdate);

    return () => {
      window.removeEventListener('visitUpdated', handleVisitUpdate);
    };
  }, []);

  useEffect(() => {
    if (filters !== debouncedFilters) return undefined;

    dispatch({ type: actionTypes.LOADING_CHANGED, loading: true });
    const controller = new AbortController();

    api
      .get(datatableWorkOrdersPath(), {
        params: kendoDatatableParams({ page, sort, filters: debouncedFilters, search }),
        signal: controller.signal
      })
      .then(({ data: workOrdersData }) => dispatch({ type: actionTypes.DATA_LOADED, data: workOrdersData }))
      .catch((err) => {
        if (axios.isCancel(err)) return;
        dispatch({ type: actionTypes.LOADING_CHANGED, loading: false });
      });

    return () => controller.abort();
  }, [page, sort, filters, debouncedFilters, search]);

  const onSelectionChange = (event) => {
    dispatch({ type: actionTypes.SELECTION_CHANGED, id: event.dataItem.id });
  };

  const onHeaderSelectionChange = (event) => {
    dispatch({ type: actionTypes.HEADER_SELECTION_CHANGED, checked: event.syntheticEvent.target.checked });
  };

  const onSelectAll = () => {
    dispatch({ type: actionTypes.ALL_SELECTED });
  };

  const massActionQueryParams = useMemo(() => {
    return kendoDatatableParams({ filters, sort, search });
  }, [filters, sort, search]);

  const onColumnsChange = (cols) => {
    dispatch({ type: actionTypes.COLUMNS_CHANGED, columns: cols.map((col) => col.id) });
  };

  const onResetFilters = () => {
    dispatch({ type: actionTypes.FILTER_CHANGED, filters: getInitialFilters(buildingId, accountId, inspectionId) });
  };

  const onResetCache = () => {
    if (!window.confirm(I18n.t('generic.are_you_sure'))) return;
    localStorage.removeItem(cacheKey);
    window.location.reload();
  };

  const getScreenInfo = () => {
    if (buildingId) return { screen: 'building', screenId: buildingId };
    if (accountId) return { screen: 'account', screenId: accountId };
    if (inspectionId) return { screen: 'inspection', screenId: inspectionId };
    return { screen: null, screenId: null };
  };

  const allMenuColumns = useMemo(() => ALL_COLUMNS.map((column) => ALL_COLUMNS_DATA[column]).filter(Boolean), []);
  const selectedMenuColumns = useMemo(
    () => columns.map((column) => ALL_COLUMNS_DATA[column]).filter(Boolean),
    [columns]
  );
  const visibleColumns = useMemo(() => {
    const safeColumns = columns.filter((column) => column !== 'id' && ALL_COLUMNS_DATA[column]);
    return ['selected', 'id', ...safeColumns].map((column) => ALL_COLUMNS_DATA[column]);
  }, [columns]);

  const headerSelectionValue = selectAll || (totalCount > 0 && data.every((workOrder) => workOrder.selected));
  const columnContextValue = useMemo(
    () => ({
      onColumnsChange,
      columnsState: selectedMenuColumns,
      columns: allMenuColumns,
      ...getScreenInfo()
    }),
    [selectedMenuColumns, allMenuColumns, onColumnsChange, buildingId, inspectionId, accountId]
  );

  return (
    <KendoLocalizationProvider>
      <ColumnMenuContext.Provider value={columnContextValue}>
        <FilterPanel onResetFilters={onResetFilters} onResetCache={onResetCache} filters={filtersList}>
          <div className="filters__group--search">
            <SearchPanel placeholder={I18n.t('filters.search')} onChange={onSearchChange} />
          </div>
        </FilterPanel>
        {loading && (
          <div className="workspace__section">
            <div className="qmb-loading--96" data-title={I18n.t('generic.loading')}>
              <svg role="img">
                <use href="/map.svg#load-spinner" />
              </svg>
            </div>
          </div>
        )}
        {!loading && (
          <CustomScrollbar>
            <Grid
              key={columns.join('-')}
              className="qmb-grid--01"
              data={data}
              total={totalCount}
              skip={page.skip}
              take={page.take}
              onPageChange={(event) => {
                dispatch({ type: actionTypes.PAGE_CHANGED, page: event.page });
              }}
              pageable={{
                type: 'input',
                pageSizes: [25, 50, 100],
                pageSizeValue: page.take
              }}
              pager={EnterKeyPager}
              sortable
              sort={sort}
              onSortChange={(event) => {
                dispatch({ type: actionTypes.SORT_CHANGED, sort: event.sort });
              }}
              dataItemKey="id"
              selectedField="selected"
              onSelectionChange={onSelectionChange}
              onHeaderSelectionChange={onHeaderSelectionChange}>
              <GridToolbar>
                <MassActions
                  state={state}
                  onSelectAll={onSelectAll}
                  onDataChanged={() => dispatch({ type: actionTypes.DATA_CHANGED })}
                  queryParams={massActionQueryParams}
                  workOrderStatusesForSelect={workOrderStatusesForSelect}
                  hasShowAllServiceReportsInCustomerPortal={hasShowAllServiceReportsInCustomerPortal}
                />
              </GridToolbar>
              {visibleColumns.map((column) => (
                <Column
                  key={column.id}
                  field={column.field}
                  title={column.title}
                  columnMenu={ColumnMenu}
                  cell={column.cell}
                  sortable={column.sortable}
                  width={column.width}
                  format={column.format}
                  headerSelectionValue={headerSelectionValue}
                  className={column.className}
                  headerClassName={column.headerClassName}
                />
              ))}
            </Grid>
          </CustomScrollbar>
        )}
      </ColumnMenuContext.Provider>
      <EditVisit />
    </KendoLocalizationProvider>
  );
}

const selectShape = PropTypes.arrayOf(
  PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.any
  })
);

Index.propTypes = {
  workOrderTypesForSelect: selectShape,
  workOrderStatusesForSelect: selectShape,
  subcontractorsForSelect: selectShape,
  visitStatusesForSelect: selectShape,
  hasShowAllServiceReportsInCustomerPortal: PropTypes.bool.isRequired,
  buildingId: PropTypes.number,
  inspectionId: PropTypes.number,
  accountId: PropTypes.number,
  subcontractorsEnabled: PropTypes.bool,
  cacheKey: PropTypes.string
};

Index.defaultProps = {
  workOrderTypesForSelect: [],
  workOrderStatusesForSelect: [],
  subcontractorsForSelect: [],
  visitStatusesForSelect: [],
  buildingId: null,
  inspectionId: null,
  accountId: null,
  subcontractorsEnabled: false,
  cacheKey: DEFAULT_CACHE_KEY
};

export default Index;
